import { DataPointWithValue } from '@src/types/DataPoint';
import { ReactElement } from 'react';

interface LableValueRenderProps {
  xPosition: number;
  yPosition: number;
  dataPoints?: DataPointWithValue[];
  pointNames:
    | string[]
    | {
        pointName: string;
        label?: string;
        ignoreInLableValueRender?: boolean;
      }[];
  showBackground?: boolean;
  backgroundColor?: string;
  borderColor?: string;
}

const BOX_PADDING_TOP = 10;
const BOX_PADDING_BOTTOM = 5;
const BOX_WIDTH = 125;
const ROW_HEIGHT = 15;
const FIRST_ROW_Y = 20;

function LableValueRender(props: LableValueRenderProps): ReactElement {
  const {
    xPosition,
    yPosition,
    dataPoints,
    pointNames,
    showBackground = true,
    backgroundColor = 'rgba(30, 30, 30, 0.4)',
    borderColor = 'rgba(255, 255, 255, 0.3)',
  } = props;

  // Check if dataPoints are defined
  if (!dataPoints || dataPoints.length === 0) {
    return (
      <g transform={`translate(${xPosition} ${yPosition})`}>
        <text
          x='25'
          y='20'
          fontSize='8'
          fill='white'
          stroke='white'
          strokeWidth='0.2'
          textAnchor='middle'
        >
          No data
        </text>
      </g>
    );
  }

  // Handle both string[] and object[] formats for pointNames
  const pointNamesArray = pointNames.map((point) =>
    typeof point === 'string' ? { pointName: point } : point,
  );

  // Match each pointName with its corresponding dataPoint
  // Filter out any data points that have "status" in their pointName
  const matchedDataPoints = pointNamesArray
    .map((pointConfig) => {
      const dataPoint = dataPoints.find(
        (dp) =>
          dp.pointName.toLowerCase() === pointConfig.pointName.toLowerCase(),
      );

      return dataPoint && !pointConfig.ignoreInLableValueRender
        ? {
            ...dataPoint,
            displayLabel: pointConfig.label || pointConfig.pointName,
          }
        : null;
    })
    .filter(Boolean);

  const boxHeight =
    matchedDataPoints.length * ROW_HEIGHT +
    BOX_PADDING_TOP +
    BOX_PADDING_BOTTOM;

  return (
    <g transform={`translate(${xPosition} ${yPosition})`}>
      {showBackground && matchedDataPoints.length > 0 && (
        <rect
          x={-10}
          y={0}
          width={BOX_WIDTH}
          height={boxHeight}
          fill={backgroundColor}
          stroke={borderColor}
          strokeWidth={0.5}
          rx={3}
          ry={3}
        />
      )}
      {matchedDataPoints.map((dataPoint, index) => (
        <text
          key={index}
          x='0'
          y={FIRST_ROW_Y + index * ROW_HEIGHT}
          fontSize='7'
          fill='white'
          stroke='white'
          strokeWidth='0.2'
          textAnchor='start'
        >
          <tspan>{dataPoint?.displayLabel}:</tspan>
          <tspan x='80'>{dataPoint?.value ?? 'N/A'}</tspan>
        </text>
      ))}
    </g>
  );
}

export default LableValueRender;
